import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ChatMessage {
  id: number;
  from: 'user' | 'ai';
  text: string;
  time: string;
}

const PRESET_MESSAGES = [
  { label: '🏖️ Bali package for 2', text: 'Hi! I\'m looking for a Bali holiday package for 2 adults in July. What options do you have?' },
  { label: '🐪 Dubai family trip', text: 'We\'re a family of 4 and want to visit Dubai in December. Can you help plan our trip?' },
  { label: '🍝 Europe group tour', text: 'I\'m organising a group of 12 for a 10-day Europe tour. Do you handle group bookings?' },
  { label: '💍 Honeymoon package', text: 'We just got engaged and are looking for a romantic honeymoon package — Maldives or Santorini.' },
];

const AI_RESPONSES: Record<string, { reply: string; followUp?: string }> = {
  bali: {
    reply: 'Great choice! 🌴 We have 3 Bali packages for July starting at AED 4,200/person including flights & 5-star resort. The most popular is our 7-night "Ubud & Seminyak" package. Shall I send you the full itinerary?',
    followUp: '✅ Lead captured • Quote ready to send • Follow-up scheduled in 24h',
  },
  dubai: {
    reply: 'Perfect timing! 🌆 December is beautiful in Dubai. For a family of 4, I\'d recommend our 6-night "Dubai Discovery" package — AED 14,800 total with a private family room at Atlantis & desert safari included. Want me to check availability?',
    followUp: '✅ Family profile saved • Package personalised • Availability checked',
  },
  europe: {
    reply: 'Absolutely! 🎒 We specialise in group tours across Europe. For 12 pax on a 10-day tour, we can arrange a dedicated coach, 4-star hotels, and a personalised itinerary. Prices start at AED 7,500/person with group discounts applied. Which cities are must-visits for your group?',
    followUp: '✅ Group enquiry logged • Sales rep assigned • Group quote initiated',
  },
  honeymoon: {
    reply: 'Congratulations! 💐 Both Maldives and Santorini are stunning honeymoon picks. The Maldives gives you a private overwater villa with crystal-clear waters from AED 6,800/couple (5 nights). Santorini offers that iconic sunset romance from AED 5,200/couple. Which vibe speaks to you both more?',
    followUp: '✅ Honeymoon profile created • Romantic add-ons suggested • Priority support assigned',
  },
};

function getAIResponse(message: string): { reply: string; followUp?: string } {
  const lower = message.toLowerCase();
  if (lower.includes('bali')) return AI_RESPONSES.bali;
  if (lower.includes('dubai') || lower.includes('family')) return AI_RESPONSES.dubai;
  if (lower.includes('europe') || lower.includes('group')) return AI_RESPONSES.europe;
  if (lower.includes('honeymoon') || lower.includes('maldives') || lower.includes('santorini') || lower.includes('engag')) return AI_RESPONSES.honeymoon;
  return {
    reply: 'Thanks for reaching out! ✈️ I\'d love to help plan your perfect trip. Could you share a few details — destination, travel dates, number of travellers, and your rough budget? I\'ll have personalised options ready for you within minutes.',
    followUp: '✅ Enquiry captured • AI processing preferences • Response sent instantly',
  };
}

function getNow() {
  return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function WhatsAppDemoSection() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 0,
      from: 'ai',
      text: 'Hi there! 👋 I\'m the TreWaz AI assistant for this travel agency. How can I help you today? Ask me about any destination, package, or holiday idea!',
      time: '09:00',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [followUpText, setFollowUpText] = useState('');
  const [showCTA, setShowCTA] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const sendMessage = (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMsg: ChatMessage = { id: Date.now(), from: 'user', text: text.trim(), time: getNow() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    setHasInteracted(true);

    const { reply, followUp } = getAIResponse(text);

    setTimeout(() => {
      const aiMsg: ChatMessage = { id: Date.now() + 1, from: 'ai', text: reply, time: getNow() };
      setMessages((prev) => [...prev, aiMsg]);
      setIsTyping(false);
      if (followUp) {
        setFollowUpText(followUp);
      }
      setTimeout(() => setShowCTA(true), 600);
    }, 1800);
  };

  const handlePreset = (text: string) => sendMessage(text);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleReset = () => {
    setMessages([
      {
        id: 0,
        from: 'ai',
        text: 'Hi there! 👋 I\'m the TreWaz AI assistant for this travel agency. How can I help you today? Ask me about any destination, package, or holiday idea!',
        time: '09:00',
      },
    ]);
    setInputValue('');
    setIsTyping(false);
    setHasInteracted(false);
    setFollowUpText('');
    setShowCTA(false);
  };

  return (
    <section id="whatsapp-demo" className="py-24 md:py-32 px-6 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none select-none" aria-hidden="true">
        <div className="absolute top-8 left-12 text-[120px]">✈</div>
        <div className="absolute top-32 right-24 text-[80px]">🏨</div>
        <div className="absolute bottom-16 left-1/4 text-[90px]">🚢</div>
        <div className="absolute bottom-28 right-1/3 text-[70px]">🗺</div>
        <div className="absolute top-1/2 left-8 text-[60px]">🧳</div>
        <div className="absolute top-16 left-1/2 text-[50px]">🌴</div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">

          {/* Left — copy */}
          <div className="w-full lg:w-[45%] flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 w-fit">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse" />
              <span className="text-xs text-emerald-300 font-medium">Live AI Demo — Try it now</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight tracking-tight">
              See the AI in Action —<br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Send a Real Message</span>
            </h2>

            <p className="text-base text-gray-400 leading-relaxed max-w-md">
              This is exactly how TreWaz responds to your customers on WhatsApp. Pick a holiday enquiry below, or type your own — and watch the AI reply in real time.
            </p>

            {/* What happens behind the scenes */}
            <div className="flex flex-col gap-3 mt-2">
              {[
                { icon: 'ri-flashlight-line', color: 'text-emerald-400', label: 'Instant reply in under 2 seconds' },
                { icon: 'ri-user-received-2-line', color: 'text-teal-400', label: 'Lead auto-captured into your CRM' },
                { icon: 'ri-calendar-check-line', color: 'text-cyan-400', label: 'Follow-up sequence triggered automatically' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg">
                    <i className={`${item.icon} ${item.color} text-sm`} />
                  </div>
                  <span className="text-sm text-gray-300">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-6 border-t border-white/10">
              {[
                { value: '< 2s', label: 'Response time' },
                { value: '94%', label: 'Leads qualified' },
                { value: '3×', label: 'More bookings' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — WhatsApp phone mockup */}
          <div className="w-full lg:w-[55%] flex justify-center">
            <div className="relative w-full max-w-[400px]">

              {/* Phone shell */}
              <div className="bg-gray-800 rounded-[40px] p-3 shadow-2xl border border-white/10">
                <div className="bg-[#ECE5DD] rounded-[30px] overflow-hidden flex flex-col h-[600px]">

                  {/* WhatsApp header */}
                  <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
                    <button
                      type="button"
                      className="text-white/70 hover:text-white transition-colors cursor-pointer"
                      onClick={handleReset}
                      title="Reset chat"
                    >
                      <i className="ri-arrow-left-line text-lg" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                      T
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-semibold leading-tight">TreWaz Travel AI</p>
                      <p className="text-emerald-200 text-xs">
                        {isTyping ? 'typing...' : 'online'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-white/70">
                      <i className="ri-phone-line text-lg" />
                      <i className="ri-more-vert-line text-lg" />
                    </div>
                  </div>

                  {/* Chat background */}
                  <div className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-2 scroll-smooth" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23c9b8a8\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M0 0h40v40H0z\'/%3E%3C/g%3E%3C/svg%3E")' }}>
                    {/* Date chip */}
                    <div className="self-center bg-white/60 text-gray-500 text-[10px] rounded-full px-3 py-0.5 font-medium mb-1">
                      TODAY
                    </div>

                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex flex-col max-w-[80%] ${msg.from === 'user' ? 'self-end items-end' : 'self-start items-start'}`}
                      >
                        <div
                          className={`px-3 py-2 rounded-xl text-sm leading-relaxed shadow-sm ${
                            msg.from === 'user'
                              ? 'bg-[#DCF8C6] text-gray-800 rounded-tr-none'
                              : 'bg-white text-gray-800 rounded-tl-none'
                          }`}
                        >
                          {msg.text}
                        </div>
                        <span className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                          {msg.time}
                          {msg.from === 'user' && (
                            <i className="ri-check-double-line text-[#34B7F1]" />
                          )}
                        </span>
                      </div>
                    ))}

                    {/* Typing indicator */}
                    {isTyping && (
                      <div className="self-start flex flex-col items-start max-w-[80%]">
                        <div className="bg-white px-4 py-3 rounded-xl rounded-tl-none shadow-sm flex items-center gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}

                    {/* Follow-up system note */}
                    {followUpText && !isTyping && (
                      <div className="self-center bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] rounded-lg px-3 py-2 font-medium text-center mt-1 max-w-[90%]">
                        <i className="ri-robot-line mr-1" />
                        {followUpText}
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Preset chips (show before interaction) */}
                  {!hasInteracted && (
                    <div className="px-3 py-2 flex flex-wrap gap-1.5 bg-[#ECE5DD] border-t border-black/5">
                      {PRESET_MESSAGES.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => handlePreset(p.text)}
                          className="bg-white text-gray-700 text-[11px] font-medium px-3 py-1.5 rounded-full border border-gray-200 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Input bar */}
                  <form onSubmit={handleSubmit} className="px-3 py-2.5 bg-[#F0F0F0] flex items-center gap-2 border-t border-black/5">
                    <button type="button" className="w-9 h-9 flex items-center justify-center text-gray-500 cursor-pointer">
                      <i className="ri-emotion-line text-xl" />
                    </button>
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type a message..."
                      disabled={isTyping}
                      className="flex-1 bg-white rounded-full px-4 py-2 text-sm text-gray-800 outline-none placeholder:text-gray-400 border border-gray-200 disabled:opacity-50"
                    />
                    <button
                      type={inputValue.trim() ? 'submit' : 'button'}
                      disabled={isTyping}
                      className="w-10 h-10 flex items-center justify-center bg-[#075E54] rounded-full text-white hover:bg-[#064d45] transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                    >
                      <i className={`${inputValue.trim() ? 'ri-send-plane-fill' : 'ri-mic-line'} text-lg`} />
                    </button>
                  </form>
                </div>
              </div>

              {/* Notch */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-full z-10" />

              {/* Floating badge */}
              {showCTA && (
                <div className="absolute -right-4 top-1/3 bg-white rounded-xl px-4 py-3 shadow-xl border border-gray-100 w-52 animate-[fadeSlideIn_0.4s_ease_both]">
                  <p className="text-xs font-semibold text-gray-800 mb-0.5">Your agency gets this</p>
                  <p className="text-xs text-gray-500 mb-3">Every enquiry handled like that, 24/7 automatically.</p>
                  <Link
                    to="/register"
                    className="block w-full text-center bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                  >
                    Start Free Trial →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom CTA bar */}
        {showCTA && (
          <div className="mt-16 bg-white/5 border border-white/10 rounded-2xl px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold text-lg">Ready to put this AI to work for your agency?</p>
              <p className="text-gray-400 text-sm mt-1">Set up in under 2 hours. No coding needed. Cancel anytime.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/register"
                className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                Start Free Trial
              </Link>
              <a
                href="#how-it-works"
                className="border border-white/20 text-white text-sm font-medium px-6 py-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer whitespace-nowrap"
              >
                See How It Works
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
